import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatApiError } from "../../lib/api";
import { inputCls, ImageManager, EMPTY, toForm, fromForm, fieldLevel, canDo, fullUrl } from "./shared";

const GENDERS = [["men", "Men"], ["women", "Women"], ["unisex", "Unisex"]];
const SORTS = [["newest", "Newest"], ["oldest", "Oldest"], ["name", "Name A–Z"], ["price", "Price"]];

function Field({ level, children }) {
  if (level === "none") return null;
  return <fieldset disabled={level !== "edit"} className="contents">{children}</fieldset>;
}

export function ProductsTab({ user, categories }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: "", gender: "", category: "", status: "", sort: "newest" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = (pg = page, f = filters) => {
    const params = { page: pg, sort: f.sort };
    if (f.q) params.q = f.q;
    if (f.gender) params.gender = f.gender;
    if (f.category) params.category = f.category;
    if (f.status) params.status = f.status;
    api.get("/admin/products/paginated", { params })
      .then((r) => { setData(r.data); setPage(r.data.page); })
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(1, filters); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (next) => { setFilters(next); setPage(1); load(1, next); };
  const resetFilters = () => applyFilters({ q: "", gender: "", category: "", status: "", sort: "newest" });

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = fromForm(form, user);
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing.id}`, payload);
      toast.success(editing === "new" ? "Product created" : "Product updated — live on the store");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently? Consider Archive instead.`)) return;
    await api.delete(`/admin/products/${p.id}`).catch((e) => toast.error(formatApiError(e)));
    toast("Product deleted");
    load();
  };

  const validCats = categories.filter((c) => c.gender === "unisex" || c.gender === form.gender || form.gender === "unisex");
  const filterCats = categories.filter((c) => !filters.gender || c.gender === "unisex" || c.gender === filters.gender);
  const activeCat = categories.find((c) => c.slug === form.category);
  const subOptions = [...(activeCat?.subs || [])];
  if (form.subcategory && !subOptions.includes(form.subcategory)) subOptions.push(form.subcategory);
  const sizeList = form.sizes.split(",").map((s) => s.trim()).filter(Boolean);
  const from = data.total === 0 ? 0 : (data.page - 1) * 10 + 1;
  const to = Math.min(data.page * 10, data.total);

  const setGender = (g) => {
    const nextCats = categories.filter((c) => c.gender === "unisex" || c.gender === g || g === "unisex");
    const stillValid = nextCats.some((c) => c.slug === form.category);
    setForm({ ...form, gender: g, category: stillValid ? form.category : (nextCats[0]?.slug || ""), subcategory: stillValid ? form.subcategory : "" });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {canDo(user, "products", "create") && (
          <button data-testid="admin-add-product-button" onClick={() => { setForm(EMPTY); setEditing("new"); }} className="vv-btn-dark">
            <Plus size={12} /> Add Product
          </button>
        )}
        <form onSubmit={(e) => { e.preventDefault(); applyFilters({ ...filters }); }} className="flex flex-wrap gap-2 flex-1">
          <input data-testid="products-search-input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search name or SKU" className={`${inputCls} !w-48`} />
          <select data-testid="products-filter-gender" value={filters.gender} onChange={(e) => applyFilters({ ...filters, gender: e.target.value, category: "" })} className={`${inputCls} !w-32`}>
            <option value="">All genders</option>
            {GENDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="products-filter-category" value={filters.category} onChange={(e) => applyFilters({ ...filters, category: e.target.value })} className={`${inputCls} !w-40`}>
            <option value="">All collections</option>
            {filterCats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select data-testid="products-filter-status" value={filters.status} onChange={(e) => applyFilters({ ...filters, status: e.target.value })} className={`${inputCls} !w-32`}>
            <option value="">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="archived">Archived</option>
          </select>
          <select data-testid="products-filter-sort" value={filters.sort} onChange={(e) => applyFilters({ ...filters, sort: e.target.value })} className={`${inputCls} !w-32`}>
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button type="submit" data-testid="products-search-button" className="vv-btn-outline-dark !px-4">Search</button>
          <button type="button" data-testid="products-reset-filters" onClick={resetFilters} className="p-2 text-vv-muted hover:text-vv-black transition-colors" aria-label="Reset filters"><RotateCcw size={14} /></button>
        </form>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-vv-muted border-b border-vv-black/10">
              <th className="py-3 pr-4">Product</th><th className="pr-4">SKU</th><th className="pr-4">Collection</th><th className="pr-4">Price</th><th className="pr-4">Stock</th><th className="pr-4">Status</th><th />
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.slug}`} className="border-b border-vv-black/5">
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 bg-vv-elevated overflow-hidden shrink-0">
                      {p.images?.[0] && <img src={fullUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-[9px] uppercase tracking-widest text-vv-stone">{p.gender || "men"}{p.featured ? " · Featured" : ""}</p>
                    </div>
                  </div>
                </td>
                <td className="pr-4 font-mono text-xs text-vv-muted">{p.sku || "—"}</td>
                <td className="pr-4 capitalize text-vv-muted">{(p.category || "").replace("-", " ")}</td>
                <td className="pr-4">
                  {p.price != null ? inr(p.price) : "—"}
                  {p.compare_at > p.price && <span className="block text-[10px] text-vv-stone line-through">{inr(p.compare_at)}</span>}
                </td>
                <td className="pr-4">{p.stock ?? "—"}</td>
                <td className="pr-4">
                  <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 ${p.status === "published" ? "bg-vv-forest text-vv-ivory" : p.status === "draft" ? "bg-vv-stone/30" : "bg-vv-black text-vv-ivory"}`}>
                    {p.status || "published"}
                  </span>
                </td>
                <td className="text-right whitespace-nowrap">
                  {canDo(user, "products", "edit") && (
                    <button data-testid={`admin-edit-${p.slug}`} onClick={() => { setForm(toForm(p)); setEditing(p); }} className="p-2" aria-label="Edit"><Pencil size={14} strokeWidth={1.5} /></button>
                  )}
                  {canDo(user, "products", "delete") && (
                    <button data-testid={`admin-delete-${p.slug}`} onClick={() => remove(p)} className="p-2 text-vv-burgundy" aria-label="Delete"><Trash2 size={14} strokeWidth={1.5} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.items.length === 0 && <p data-testid="products-empty" className="text-sm text-vv-muted py-10">No products match these filters.</p>}
      </div>

      <div data-testid="products-pagination" className="flex items-center justify-between mt-6">
        <p data-testid="products-page-info" className="text-[11px] uppercase tracking-[0.2em] text-vv-muted">
          Showing {from}–{to} of {data.total} products
        </p>
        <div className="flex items-center gap-2">
          <button data-testid="products-prev-page" disabled={data.page <= 1} onClick={() => load(page - 1)}
            className="border border-vv-black/20 p-2 disabled:opacity-30" aria-label="Previous page"><ChevronLeft size={14} /></button>
          <span className="text-xs font-mono px-2">{data.page} / {data.pages}</span>
          <button data-testid="products-next-page" disabled={data.page >= data.pages} onClick={() => load(page + 1)}
            className="border border-vv-black/20 p-2 disabled:opacity-30" aria-label="Next page"><ChevronRight size={14} /></button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setEditing(null)} />
          <form data-testid="admin-product-form" onSubmit={save} className="relative bg-white w-full max-w-2xl p-8 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display text-2xl">{editing === "new" ? "New Product" : "Edit Product"}</p>
              <button type="button" data-testid="admin-form-close" onClick={() => setEditing(null)} aria-label="Close"><X size={20} strokeWidth={1.25} /></button>
            </div>

            <Field level={fieldLevel(user, "details")}>
              <input data-testid="form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name" className={inputCls} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Collection for</label>
                  <select data-testid="form-gender" value={form.gender} onChange={(e) => setGender(e.target.value)} className={`${inputCls} mt-2`}>
                    {GENDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Collection</label>
                  <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, subcategory: "" })} className={`${inputCls} mt-2`}>
                    {validCats.map((c) => <option key={c.slug} value={c.slug}>{c.name}{c.gender !== "unisex" ? ` (${c.gender})` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Subcollection</label>
                  <select data-testid="form-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className={`${inputCls} mt-2`}>
                    <option value="">None</option>
                    {subOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <input data-testid="form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="SKU (e.g. VV-TEE-001)" className={`${inputCls} font-mono self-end`} />
              </div>
            </Field>

            <Field level={fieldLevel(user, "price")}>
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="form-price" required type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Selling price (INR)" className={inputCls} />
                <input data-testid="form-compare-at" type="number" min="0" value={form.compare_at} onChange={(e) => setForm({ ...form, compare_at: e.target.value })} placeholder="Original price ₹ (shows strike-through)" className={inputCls} />
              </div>
            </Field>

            <Field level={fieldLevel(user, "cost")}>
              <input data-testid="form-cost-price" type="number" min="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} placeholder="Cost price ₹ (internal — hidden from staff without cost access)" className={inputCls} />
            </Field>

            <Field level={fieldLevel(user, "stock")}>
              <input data-testid="form-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Total stock" className={inputCls} />
            </Field>

            <Field level={fieldLevel(user, "visibility")}>
              <div className="grid grid-cols-2 gap-4 items-center">
                <select data-testid="form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                  <option value="published">Published — visible on store</option>
                  <option value="draft">Draft — hidden from store</option>
                  <option value="archived">Archived</option>
                </select>
                <label className="flex items-center gap-3 text-sm">
                  <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#111111]" />
                  Featured on homepage
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4 items-center border border-vv-black/10 px-4 py-3">
                <label className="flex items-center gap-3 text-sm">
                  <input data-testid="form-new-arrival" type="checkbox" checked={!!form.new_arrival} onChange={(e) => setForm({ ...form, new_arrival: e.target.checked })} className="accent-[#111111]" />
                  Show in New Arrivals
                </label>
                <input data-testid="form-new-arrival-order" type="number" min="0" value={form.new_arrival_order ?? ""}
                  onChange={(e) => setForm({ ...form, new_arrival_order: e.target.value })}
                  placeholder="Display order (blank = newest first)" className={inputCls} disabled={!form.new_arrival} />
              </div>
            </Field>

            <Field level={fieldLevel(user, "content")}>
              <textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={3} className={inputCls} />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="form-fabric" value={form.fabric} onChange={(e) => setForm({ ...form, fabric: e.target.value })} placeholder="Fabric (e.g. 100% combed cotton)" className={inputCls} />
                <input data-testid="form-gsm" value={form.gsm} onChange={(e) => setForm({ ...form, gsm: e.target.value })} placeholder="GSM (optional)" className={inputCls} />
                <input data-testid="form-wash-care" value={form.wash_care} onChange={(e) => setForm({ ...form, wash_care: e.target.value })} placeholder="Wash care (e.g. Cold machine wash)" className={inputCls} />
              </div>
              <div className="border border-vv-black/10 p-4 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">SEO</p>
                <input data-testid="form-seo-title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder="SEO title (defaults to product name)" className={inputCls} />
                <textarea data-testid="form-seo-description" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} placeholder="SEO description for search engines" rows={2} className={inputCls} />
              </div>
            </Field>

            <Field level={fieldLevel(user, "details")}>
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="form-fit" value={form.fit} onChange={(e) => setForm({ ...form, fit: e.target.value })} placeholder="Fit (e.g. Oversized)" className={inputCls} />
                <input data-testid="form-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags: new, bestseller" className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Colours — Name:#hex, comma separated</label>
                <input data-testid="form-colors" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className={`${inputCls} mt-2`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Sizes — comma separated</label>
                <input data-testid="form-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className={`${inputCls} mt-2`} />
              </div>
            </Field>

            {sizeList.length > 0 && fieldLevel(user, "stock") !== "none" && (
              <Field level={fieldLevel(user, "stock")}>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">Stock per size (optional)</label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {sizeList.map((s) => (
                      <div key={s} className="flex items-center gap-1.5 border border-vv-black/15 px-2 py-1">
                        <span className="text-[10px] uppercase tracking-wider text-vv-muted">{s}</span>
                        <input data-testid={`form-size-stock-${s.toLowerCase().replace(/\s/g, "-")}`} type="number" min="0"
                          value={form.size_stock[s] ?? ""} placeholder="—"
                          onChange={(e) => setForm({ ...form, size_stock: { ...form.size_stock, [s]: e.target.value === "" ? undefined : parseInt(e.target.value) } })}
                          className="w-14 bg-transparent outline-none text-xs text-center" />
                      </div>
                    ))}
                  </div>
                </div>
              </Field>
            )}

            <Field level={fieldLevel(user, "images")}>
              <ImageManager images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} disabled={fieldLevel(user, "images") !== "edit"} />
            </Field>

            <button data-testid="form-save-button" type="submit" className="vv-btn-dark w-full">Save Product</button>
          </form>
        </div>
      )}
    </div>
  );
}
